import React from 'react';
import {useIntl} from 'react-intl';
import styled from 'styled-components';
import {Button, EMPHASIS, SubHeading, Text} from '@forecast-it/design-system';
import {SIZE, XeroIcon} from '@/web-components';
import InlineLoader from '../../../shared/components/inline-loader/inline_loader';
import {XeroOrganization} from '../export-defaults/xeroExportDefaults';
import {ConfigPageCard} from './XeroConfigPageStyled';

export type ConnectedXeroOrganization = XeroOrganization & {inaccessible?: boolean};

type Props = {
	organizations: ReadonlyArray<ConnectedXeroOrganization>;
	fetching: boolean;
	onAdd: () => void;
	onReconnect: () => void;
	onRemove: (tenantId: string) => void;
	onHelp: () => void;
};

const Intro = styled.div`
	display: flex;
	gap: ${props => props.theme.sys.spacing['600']};
`;

const IntroText = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${props => props.theme.sys.spacing['400']};
	min-width: 0;
`;

const divider = (props: {theme: {sys: {size: {borderwidth: {thin: string}}; color: {border: {decorative: string}}}}}) =>
	`${props.theme.sys.size.borderwidth.thin} solid ${props.theme.sys.color.border.decorative}`;

const OrganizationsHeading = styled.div`
	margin-top: ${props => props.theme.sys.spacing['800']};
	padding-bottom: ${props => props.theme.sys.spacing['200']};
	border-bottom: ${divider};
`;

// Wraps so a long organisation name pushes its buttons onto the next line instead of off the card.
const OrganizationRow = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	justify-content: space-between;
	gap: ${props => props.theme.sys.spacing['300']};
	padding: ${props => props.theme.sys.spacing['300']} 0;
	border-bottom: ${divider};
	overflow-wrap: anywhere;
`;

const RowActions = styled.div`
	display: flex;
	gap: ${props => props.theme.sys.spacing['200']};
`;

const Actions = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: ${props => props.theme.sys.spacing['200']};
	margin-top: ${props => props.theme.sys.spacing['450']};
`;

/**
 * The intro and the connected-organisation list of Admin > Integrations > Xero in the new design
 * (T4-205). Presentational only: every action is the page's own handler, passed in, so connecting,
 * reconnecting, removing and the help link behave exactly as on the legacy page.
 */
export const XeroOrganizationsSection = ({organizations, fetching, onAdd, onReconnect, onRemove, onHelp}: Props) => {
	const {formatMessage} = useIntl();
	// One string with a blank line between paragraphs in English; other languages have one paragraph.
	const paragraphs = formatMessage({id: 'settings.app_catalog.xero_description'}).split('\n\n');

	return (
		<ConfigPageCard className="xero-config-page-card" data-cy="xero-organizations-section">
			<Intro>
				<XeroIcon size={SIZE.EXTRA_BIG} />
				<IntroText>
					{/* A static split of one string: the index is a stable key, and text could repeat. */}
					{paragraphs.map((paragraph, index) => (
						<Text key={index} size="2">
							{paragraph}
						</Text>
					))}
				</IntroText>
			</Intro>

			<OrganizationsHeading>
				<SubHeading size="2">{formatMessage({id: 'common.organization'})}</SubHeading>
			</OrganizationsHeading>

			{fetching ? (
				<InlineLoader />
			) : (
				<>
					{organizations.map(organization => (
						<OrganizationRow key={organization.tenantId} data-cy="xero-organization-row">
							<SubHeading size="1">{organization.organization}</SubHeading>
							<RowActions>
								{organization.inaccessible ? (
									<Button
										emphasis={EMPHASIS.MEDIUM}
										onPress={onReconnect}
										data-cy="xero-organization-reconnect"
									>
										{formatMessage({id: 'common.reconnect'})}
									</Button>
								) : null}
								<Button
									emphasis={EMPHASIS.MEDIUM}
									onPress={() => onRemove(organization.tenantId)}
									data-cy="xero-organization-remove"
								>
									{formatMessage({id: 'common.remove'})}
								</Button>
							</RowActions>
						</OrganizationRow>
					))}
					<Actions>
						<Button emphasis={EMPHASIS.HIGH} onPress={onAdd} data-cy="xero-add-organization">
							{formatMessage({id: 'settings.app_catalog.xero.add_organization'})}
						</Button>
						<Button emphasis={EMPHASIS.LOW} onPress={onHelp} data-cy="xero-help-center">
							{formatMessage({id: 'settings.app_catalog.help_button'})}
						</Button>
					</Actions>
				</>
			)}
		</ConfigPageCard>
	);
};
